/**
 * Deterministic UTC-based date formatting utilities.
 *
 * We avoid Intl formatting here because Node and modern browsers diverge on
 * `timeZoneName` output ("GMT" vs "UTC"), which breaks React hydration. These
 * helpers rely on manual UTC formatting so the markup is identical on server
 * and client renders.
 */

import { isDate, validateDate } from './date';

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

const pad2 = (value: number) => value.toString().padStart(2, '0');

// `Intl.RelativeTimeFormat` is deterministic when we pin the locale to `en`, so hydration remains stable.
const RELATIVE_TIME_FORMATTER = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

const relativeTimeUnits: Array<{ unit: Intl.RelativeTimeFormatUnit; seconds: number }> = [
  { unit: 'year', seconds: 60 * 60 * 24 * 365 },
  { unit: 'month', seconds: 60 * 60 * 24 * 30 },
  { unit: 'week', seconds: 60 * 60 * 24 * 7 },
  { unit: 'day', seconds: 60 * 60 * 24 },
  { unit: 'hour', seconds: 60 * 60 },
  { unit: 'minute', seconds: 60 },
  { unit: 'second', seconds: 1 },
];

const formatRelativeTime = (date: Date): string => {
  const now = Date.now();
  const diffInSecondsFloat = (date.getTime() - now) / 1000;
  if (Math.abs(diffInSecondsFloat) < 60) {
    return 'just now';
  }
  const diffInSeconds = Math.round(diffInSecondsFloat);
  for (const { unit, seconds } of relativeTimeUnits) {
    if (Math.abs(diffInSeconds) >= seconds || unit === 'second') {
      const value = Math.round(diffInSeconds / seconds);
      if (value === 0 && unit === 'second') {
        return 'just now';
      }
      return RELATIVE_TIME_FORMATTER.format(value, unit);
    }
  }
  return RELATIVE_TIME_FORMATTER.format(0, 'second');
};

export function coerceDate(value: Date | string | number | null | undefined): Date | null {
  if (value === undefined) {
    return null;
  }
  if (isDate(value)) {
    return validateDate(value);
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

export function parseUtcDate(value: Date | string | number | null | undefined): Date | null {
  const parsed = coerceDate(value);
  return parsed ? validateDate(parsed) : null;
}

export function formatUtcDate(value: Date | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const date = validateDate(value);
  if (!date) {
    return null;
  }
  const month = MONTHS_SHORT[date.getUTCMonth()];
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  return `${month} ${day}, ${year}`;
}

export function formatUtcDateTime(value: Date | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const date = validateDate(value);
  if (!date) {
    return null;
  }
  const month = MONTHS_LONG[date.getUTCMonth()];
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  const hours = pad2(date.getUTCHours());
  const minutes = pad2(date.getUTCMinutes());
  const seconds = pad2(date.getUTCSeconds());
  return `${month} ${day}, ${year} ${hours}:${minutes}:${seconds} UTC`;
}

export function formatUtcDateTimeShort(value: Date | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const date = validateDate(value);
  if (!date) {
    return null;
  }
  const month = MONTHS_SHORT[date.getUTCMonth()];
  const day = date.getUTCDate();
  const hours = pad2(date.getUTCHours());
  const minutes = pad2(date.getUTCMinutes());
  return `${month} ${day}, ${hours}:${minutes} UTC`;
}

export function formatUtcDateParts(
  value: Date | null | undefined
): { short: string; full: string; relative: string } | null {
  if (!value) {
    return null;
  }
  const date = validateDate(value);
  if (!date) {
    return null;
  }
  const short = formatUtcDate(date);
  const full = formatUtcDateTime(date);
  if (!(short && full)) {
    return null;
  }
  return { short, full, relative: formatRelativeTime(date) };
}
